import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import WorkOrderLink from "./Component/WorkOrderEventLog/WorkOrderLink";
import { rememberReturnTo } from "./routes/returnTo";

import Login from "./Pages/Login/Login";
import NotFound from "./Pages/NotFound/NotFound";
import Footer from "./Component/Footer/Footer";
import Navbar from "./Component/NavBar/Navbar";
import PermissionRoute from "./Component/PermissionRoute/PermissionRoute";
import PageLoader from "./components/PageLoader";
import Layout from "./Layout/Layout";
import { SidebarProvider } from "./Context/SidebarContext";
import { ContractProvider } from "./Context/ContractContext";
import { getUserSession } from "./function/AuthStorage";
import { startAccessSync } from "./permissions/access";
import "./assets/css/all.css";

import {
  ALL_APPROVE_PERMS,
  ALL_DELETE_PERMS,
  DAILY_EXECUTION_PERMS,
  PROJECT_VIEW_PERMS,
  ADMIN_LAYOUT_TYPES,
  NAVBAR_USER_TYPES,
  USER_TYPES,
  getFallback,
} from "./routes/permissions";

import * as Pages from "./routes/lazyPages";

const {
  DashboardShell, SearchRequests, Orders, DailyExecutionPage, AccessManagementPage,
  ContractHubPage, ExecutionTrackingPage, MaintenanceCreatePage, EmergencyCreatePage, RehabilitationCreatePage, PrivateProjectCreatePage, DailyWorkPlanPage, TodayWorkPage, ExecutionTeamsPage, ConstructionCreatePage, WorkOrderDraftsPage, EmployeeHubPage,
  MonitoringPage, PdcIndicatorsPage, InternalIndicatorsPage, WorkOrderImportPage, Project, AddAccount, Accounts, Engineers, MainPage, Subscribers,
  SubmitApplication, ArchivedRequests, Contactus, About, AllProject, SpecialProjects,
  OperateMaintains, EngStatcis, Consultants, DeleteOrders,
  NotificationPage, Areas, Branches, Offices, Vacations, RehabilitationWorks,
  EngProfile, EngNotification, AcceptDeteleOrders,
  TaskType, AdminProjects, AdminBranchData, ResetPassword, RequestsWaiting,
  OfficeMangerProfile, AllAdminPrivateProject, BootInfo, Custodies,
  CheckAttendanceAdmin, EngAttendance, MyVacations, CustodiesEngContent,
  ProjectParties, ProjectOwners, AdminProjectCreate, WorkOrderDetailsPage,
} = Pages;

// ─── Routes اللي بيظهر فيها الـ Navbar والـ Footer (مهندس / مقاول) ─────────
const NAVBAR_PREFIXES = ["/sub-page/", "/requests/"];

const NAVBAR_ROUTES = [
  "/main-page", "/submit-application", "/submit-application/:id",
  "/contactus", "/about", "/projects", "/electricRequests",
  "/special-projects", "/special-projects/:id",
  "/maintain-projects", "/maintain-projects/:id",
  "/all-project", "/eng-profile", "/eng-notification",
  "/eng-attendance", "/boot-info", "/myvacations", "/custodieseng",
  "/rehabilitation-works", "/rehabilitation-works/:id",
  "/emergency-projects", "/emergency-projects/:id",
  "/construction-projects", "/construction-projects/:id",
  "/statics",
].map((route) => new RegExp(`^${route.replace(":id", "[0-9a-zA-Z-]+")}$`, "i"));

// ─── Route wrapper helpers ────────────────────────────────────────────────
// S  → Suspense محلي: الـ Layout (السايدبار) يفضل ظاهر أثناء تحميل الصفحة
const S = (element) => (
  <Suspense fallback={<PageLoader />}>{element}</Suspense>
);

// P  → صلاحية واحدة مطلوبة
const P = (permission, element, fallback) => (
  <PermissionRoute permission={permission} fallback={fallback}>
    {S(element)}
  </PermissionRoute>
);

// PA → أي صلاحية من القايمة كفاية
const PA = (anyOf, element, fallback) => (
  <PermissionRoute anyOf={anyOf} fallback={fallback}>
    {S(element)}
  </PermissionRoute>
);

// ═════════════════════════════════════════════════════════════════════════
function App() {
  // الـ session بتتقرا مرة واحدة في أول render، عشان الـ refresh ما يعرضش NotFound
  const [userData, setUserData] = useState(() => getUserSession());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // الصلاحيات الفعلية تُحدَّث من الخلفية (دخول، كل دقيقة، عودة للنافذة) — سحب الصلاحية يسري فوراً
  useEffect(() => {
    if (userData?.token) startAccessSync();
  }, [userData]);

  // لو مفيش session ارجع للـ login
  useEffect(() => {
    const user = getUserSession();
    if (!user && location.pathname !== "/" && location.pathname !== "/reset-password") {
      // رابط أمر العمل من البريد: يُفتح بعد تسجيل الدخول مباشرة
      rememberReturnTo(location.pathname + location.search);
      navigate("/", { replace: true });
    }
  }, [navigate, location.pathname, location.search]);

  const userType = userData?.userType;
  const fb = getFallback(userType);

  const showNavbarAndFooter = useMemo(() => {
    if (!NAVBAR_USER_TYPES.includes(userType)) return false;
    const path = location.pathname;
    const lower = path.toLowerCase();
    return (
      NAVBAR_PREFIXES.some((prefix) => lower.startsWith(prefix)) ||
      NAVBAR_ROUTES.some((re) => re.test(path))
    );
  }, [userType, location.pathname]);

  const isAdminLayoutUser = ADMIN_LAYOUT_TYPES.includes(userType);
  const isEng = userType === USER_TYPES.ENG;
  const isContractor = userType === USER_TYPES.CONTRACTOR;

  return (
    <ContractProvider>
      <div>
        {showNavbarAndFooter && <Navbar userData={userData} />}

        <div className={showNavbarAndFooter ? "pt-28" : ""}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ── Public ── */}
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<Login setUserData={setUserData} />} />

              {/* رابط أمر العمل الثابت (البريد والإشعارات) → صفحة أمر العمل حسب نوع المستخدم */}
              {userData && <Route path="/work-order/:id" element={<WorkOrderLink userType={userType} />} />}

              {/* ══════════════════════════════════════════════════════════
                  Admin / Supervisor / Office Manager
              ══════════════════════════════════════════════════════════ */}
              {userData && isAdminLayoutUser && (
                <Route
                  element={
                    <SidebarProvider>
                      <Layout />
                    </SidebarProvider>
                  }
                >
                  {/* لوحة المؤشرات العامة: صلاحية «Dashboard.View» (رُحّلت لمن كان يفتحها بنوع حسابه) */}
                  <Route path="/home-page" element={P("Dashboard.View", <DashboardShell />, fb)} />

                  {/* بحث الطلبات → أي View من modules المشاريع */}
                  <Route path="/search-requests"
                    element={PA(PROJECT_VIEW_PERMS, <SearchRequests />, fb)} />

                  <Route path="/admin-projects/:type"
                    element={PA(PROJECT_VIEW_PERMS, <AdminProjectCreate userData={userData} />, fb)} />

                  {/* أوامر العمل → أي View من modules المشاريع أو أنواع الأوامر */}
                  <Route path="/orders"
                    element={PA([...PROJECT_VIEW_PERMS, "WorkOrderType.View"], <Orders />, fb)} />

                  {/* تحديث التنفيذ اليومي */}
                  <Route path="/daily-execution"
                    element={PA(DAILY_EXECUTION_PERMS, <DailyExecutionPage />, fb)} />

                  {/* لوحة المتابعة والمؤشّرات */}
                  <Route path="/monitoring"
                    element={P("ContractWorkflow.View", <MonitoringPage />, fb)} />

                  {/* مؤشرات PDC — لوحة مؤشرات أداء أوامر العمل (قراءة فقط) */}
                  <Route path="/pdc-indicators"
                    element={P("PDCIndicators.View", <PdcIndicatorsPage />, fb)} />
                  <Route path="/internal-indicators"
                    element={P("InternalIndicators.View", <InternalIndicatorsPage />, fb)} />

                  {/* قالب إنشاء أمر عمل الإنشاءات */}
                  <Route path="/work-orders/new/construction"
                    element={P("Construction.Create", <ConstructionCreatePage />, fb)} />

                  {/* إنشاء أوامر عمل الصيانة والطوارئ وأعمال التأهيل والمشاريع الخاصة — صفحة مستقلة لكل نوع */}
                  <Route path="/work-orders/new/maintenance"
                    element={P("Maintenance.Create", <MaintenanceCreatePage />, fb)} />
                  <Route path="/work-orders/new/emergency"
                    element={P("Emergency.Create", <EmergencyCreatePage />, fb)} />
                  <Route path="/work-orders/new/rehabilitation"
                    element={P("NewProject.Create", <RehabilitationCreatePage />, fb)} />
                  <Route path="/work-orders/new/private"
                    element={P("PrivateProject.Create", <PrivateProjectCreatePage />, fb)} />

                  {/* مسودات أوامر العمل — استكمال ما حُفظ */}
                  <Route path="/work-orders/drafts"
                    element={P("Construction.Create", <WorkOrderDraftsPage />, fb)} />

                  {/* الاستيراد الذكي لأوامر العمل من Excel (معاينة واعتماد قبل الإنشاء) */}
                  <Route path="/work-orders/import"
                    element={P("WorkOrders.ExcelImport.View", <WorkOrderImportPage />, fb)} />

                  {/* متابعة التنفيذ */}
                  <Route path="/execution-tracking"
                    element={P("ContractWorkflow.View", <ExecutionTrackingPage />, fb)} />

                  {/* جدول الأعمال اليومية وأعمال تُنفذ اليوم وفرق التنفيذ */}
                  <Route path="/daily-work-plan"
                    element={P("DailyWork.View", <DailyWorkPlanPage />, fb)} />
                  <Route path="/today-work"
                    element={PA(["DailyWork.ViewToday", "DailyWork.View"], <TodayWorkPage />, fb)} />
                  <Route path="/execution-teams"
                    element={PA(["ExecutionTeams.View", "ExecutionTeams.Manage"], <ExecutionTeamsPage />, fb)} />

                  {/* إعدادات العقد — مركز واحد: العقود، الأقسام والسلال، الأنواع، الفريق، الأسعار */}
                  <Route path="/contract-settings"
                    element={P("ContractWorkflow.View", <ContractHubPage />, fb)} />

                  {/* الروابط القديمة تُحوّل إلى المركز بدل أن تنكسر على من حفظها */}
                  <Route path="/contract-workflow" element={<Navigate to="/contract-settings" replace />} />
                  <Route path="/settings" element={<Navigate to="/contract-settings" replace />} />

                  {/* إدارة الصلاحيات */}
                  <Route path="/access-management"
                    element={P("Users.ManagePermissions", <AccessManagementPage />, fb)} />

                  {/* المسار القديم /add-permission حلّت محله /access-management */}
                  <Route path="/add-permission" element={<Navigate to="/access-management" replace />} />
                  <Route path="/settings/permissions" element={<Navigate to="/access-management" replace />} />

                  {/* تفاصيل مشروع → أي View */}
                  <Route path="/project/:type/:id"
                    element={PA(PROJECT_VIEW_PERMS, <Project userData={userData} />, fb)} />

                  {/* المهندسون → Employees.View */}
                  <Route path="/engineers"
                    element={P("Employees.View", <Engineers />, fb)} />

                  {/* الاستشاريون → Consultant.View */}
                  <Route path="/consultants"
                    element={P("Consultant.View", <Consultants />, fb)} />

                  {/* كل المشاريع → أي View */}
                  <Route path="/admin-projects"
                    element={PA(PROJECT_VIEW_PERMS, <AdminProjects />, fb)} />

                  {/* المشاريع الخاصة → PrivateProject.View */}
                  <Route path="/all-admin-private-project"
                    element={P("PrivateProject.View", <AllAdminPrivateProject />, fb)} />

                  {/* المشاريع المحذوفة → أي Delete */}
                  <Route path="/deleted-projects"
                    element={PA(ALL_DELETE_PERMS, <DeleteOrders />, fb)} />

                  {/* طلبات انتظار الموافقة → أي Approve */}
                  <Route path="/accept-requests"
                    element={PA(ALL_APPROVE_PERMS, <RequestsWaiting />, fb)} />

                  {/* الموافقة على الحذف → أي Delete */}
                  <Route path="/accept-delete"
                    element={PA(ALL_DELETE_PERMS, <AcceptDeteleOrders />, fb)} />

                  {/* بيانات الفرع → Branch.View */}
                  <Route path="/admin-branch-data"
                    element={P("Branch.View", <AdminBranchData />, fb)} />

                  {/* ملف مدير المكتب → Office.View */}
                  <Route path="/manager-profile"
                    element={P("Office.View", <OfficeMangerProfile />, fb)} />

                  {/* أنواع المهام → WorkOrderType.View */}
                  <Route path="/tasktype"
                    element={P("WorkOrderType.View", <TaskType />, fb)} />

                  {/* إضافة / تعديل حساب → Users.Create */}
                  <Route path="/add-account"
                    element={P("Users.Create", <AddAccount />, fb)} />
                  <Route path="/add-account/:id"
                    element={P("Users.Create", <AddAccount />, fb)} />

                  {/* عرض الحسابات → Users.View */}
                  <Route path="/accounts"
                    element={P("Users.View", <Accounts />, fb)} />

                  {/* الإشعارات → Notifications.View */}
                  <Route path="/notification"
                    element={P("Notifications.View", <NotificationPage />, fb)} />

                  {/* الأحياء → Neighborhood.View */}
                  <Route path="/district"
                    element={P("Neighborhood.View", <Areas />, fb)} />

                  {/* العهدة → Custody.View */}
                  <Route path="/custodies"
                    element={P("Custody.View", <Custodies />, fb)} />

                  {/* الفروع → Branch.View */}
                  <Route path="/branches"
                    element={P("Branch.View", <Branches />, fb)} />

                  {/* المكاتب → Office.View */}
                  <Route path="/offices"
                    element={P("Office.View", <Offices />, fb)} />

                  {/* قسم الموظفين الموحّد: السجلّ والملفات والمستندات والإجازات
                      والتقويم والحضور والاستيراد → Employees.View */}
                  <Route path="/employees"
                    element={P("Employees.View", <EmployeeHubPage />, fb)} />

                  {/* الإجازات → LeaveRequest.View */}
                  <Route path="/vacations"
                    element={P("LeaveRequest.View", <Vacations />, fb)} />

                  {/* الحضور → Attendance.View */}
                  <Route path="/check-attendance"
                    element={P("Attendance.View", <CheckAttendanceAdmin />, fb)} />

                  <Route path="/project-parties"
                    element={P("ProjectParty.View", <ProjectParties />, fb)} />

                  <Route path="/project-owners"
                    element={P("ProjectOwner.View", <ProjectOwners />, fb)} />

                  {/* الملف الشخصي: بيانات المستخدم نفسه — متاح لكل مستخدم مسجّل */}
                  <Route path="/profile" element={S(<EngProfile />)} />
                </Route>
              )}

              {/* ══════════════════════════════════════════════════════════
                  Engineer routes
              ══════════════════════════════════════════════════════════ */}
              {isEng && (
                <>
                  {/* صفحات مفتوحة لكل المهندسين */}
                  <Route path="/main-page" element={<MainPage userData={userData} />} />
                  <Route path="/sub-page/:name" element={<Subscribers />} />
                  <Route path="/submit-application" element={<SubmitApplication userData={userData} />} />
                  <Route path="/submit-application/:id" element={<SubmitApplication userData={userData} />} />
                  <Route path="/statics" element={<EngStatcis />} />
                  <Route path="/eng-notification" element={<EngNotification />} />
                  <Route path="/requests/:namepage/:name" element={<ArchivedRequests userData={userData} />} />
                  <Route path="/electricRequests" element={<OperateMaintains />} />
                  <Route path="/boot-info" element={<BootInfo userData={userData} />} />
                  <Route path="/eng-profile" element={<EngProfile userData={userData} />} />
                  <Route path="/contactus" element={<Contactus />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/projects" element={<AllProject userData={userData} />} />

                  {/* صفحات محمية بـ Permission */}
                  <Route path="/eng-attendance"
                    element={P("Attendance.View", <EngAttendance />, "/main-page")} />
                  <Route path="/myvacations"
                    element={P("LeaveRequest.View", <MyVacations />, "/main-page")} />
                  <Route path="/custodieseng"
                    element={P("Custody.View", <CustodiesEngContent />, "/main-page")} />

                  {/* صفحة الإنشاء القديمة: «إضافة» يفتح صفحة إنشاء أمر العمل الجديدة (كما في الإنشاءات) */}
                  <Route path="/rehabilitation-works"
                    element={P("NewProject.Create", <Navigate to="/work-orders/new/rehabilitation" replace />, "/main-page")} />
                  <Route path="/rehabilitation-works/:id"
                    element={P("NewProject.View", <RehabilitationWorks userData={userData} />, "/main-page")} />

                  <Route path="/emergency-projects"
                    element={P("Emergency.Create", <Navigate to="/work-orders/new/emergency" replace />, "/main-page")} />
                  <Route path="/emergency-projects/:id"
                    element={P("Emergency.View", <WorkOrderDetailsPage typeKey="emergency" />, "/main-page")} />

                  {/* معالج إنشاء أمر العمل متاح للمهندس أيضاً، ويحكمه العقد المسموح له */}
                  <Route path="/work-orders/new/construction"
                    element={P("Construction.Create", <ConstructionCreatePage />, "/main-page")} />

                  {/* إنشاء أوامر عمل الصيانة والطوارئ وأعمال التأهيل والمشاريع الخاصة — صفحة مستقلة لكل نوع */}
                  <Route path="/work-orders/new/maintenance"
                    element={P("Maintenance.Create", <MaintenanceCreatePage />, "/main-page")} />
                  <Route path="/work-orders/new/emergency"
                    element={P("Emergency.Create", <EmergencyCreatePage />, "/main-page")} />
                  <Route path="/work-orders/new/rehabilitation"
                    element={P("NewProject.Create", <RehabilitationCreatePage />, "/main-page")} />
                  <Route path="/work-orders/new/private"
                    element={P("PrivateProject.Create", <PrivateProjectCreatePage />, "/main-page")} />
                  <Route path="/work-orders/drafts"
                    element={P("Construction.Create", <WorkOrderDraftsPage />, "/main-page")} />

                  {/* صفحة الإنشاء القديمة حُذفت: «إضافة طلب» يفتح معالج إنشاء أمر العمل */}
                  <Route path="/construction-projects"
                    element={P("Construction.Create", <Navigate to="/work-orders/new/construction" replace />, "/main-page")} />
                  <Route path="/construction-projects/:id"
                    element={P("Construction.View", <WorkOrderDetailsPage typeKey="construction" />, "/main-page")} />

                  <Route path="/special-projects"
                    element={P("PrivateProject.Create", <Navigate to="/work-orders/new/private" replace />, "/main-page")} />
                  <Route path="/special-projects/:id"
                    element={P("PrivateProject.View", <SpecialProjects userData={userData} />, "/main-page")} />

                  <Route path="/maintain-projects"
                    element={P("Maintenance.Create", <Navigate to="/work-orders/new/maintenance" replace />, "/main-page")} />
                  <Route path="/maintain-projects/:id"
                    element={P("Maintenance.View", <WorkOrderDetailsPage typeKey="maintenance" />, "/main-page")} />
                </>
              )}

              {/* ══════════════════════════════════════════════════════════
                  Contractor routes
              ══════════════════════════════════════════════════════════ */}
              {isContractor && (
                <>
                  <Route path="/projects" element={<AllProject userData={userData} />} />
                  {/* إنشاء أمر عمل الإنشاءات بالمعالج الجديد فقط؛ النموذج القديم للتعديل وحده */}
                  <Route path="/work-orders/new/construction"
                    element={P("Construction.Create", <ConstructionCreatePage />, "/projects")} />

                  {/* إنشاء أوامر عمل الصيانة والطوارئ وأعمال التأهيل والمشاريع الخاصة — صفحة مستقلة لكل نوع */}
                  <Route path="/work-orders/new/maintenance"
                    element={P("Maintenance.Create", <MaintenanceCreatePage />, "/projects")} />
                  <Route path="/work-orders/new/emergency"
                    element={P("Emergency.Create", <EmergencyCreatePage />, "/projects")} />
                  <Route path="/work-orders/new/rehabilitation"
                    element={P("NewProject.Create", <RehabilitationCreatePage />, "/projects")} />
                  <Route path="/work-orders/new/private"
                    element={P("PrivateProject.Create", <PrivateProjectCreatePage />, "/projects")} />
                  <Route path="/work-orders/drafts"
                    element={P("Construction.Create", <WorkOrderDraftsPage />, "/projects")} />
                  <Route path="/construction-projects" element={<Navigate to="/work-orders/new/construction" replace />} />
                  <Route path="/construction-projects/:id" element={<WorkOrderDetailsPage typeKey="construction" />} />
                  <Route path="/maintain-projects" element={<Navigate to="/work-orders/new/maintenance" replace />} />
                  <Route path="/maintain-projects/:id" element={<WorkOrderDetailsPage typeKey="maintenance" />} />
                  <Route path="/emergency-projects" element={<Navigate to="/work-orders/new/emergency" replace />} />
                  <Route path="/emergency-projects/:id" element={<WorkOrderDetailsPage typeKey="emergency" />} />
                  <Route path="/rehabilitation-works" element={<Navigate to="/work-orders/new/rehabilitation" replace />} />
                  <Route path="/rehabilitation-works/:id" element={<RehabilitationWorks userData={userData} />} />
                  <Route path="/special-projects" element={<Navigate to="/work-orders/new/private" replace />} />
                  <Route path="/special-projects/:id" element={<SpecialProjects userData={userData} />} />
                </>
              )}

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>

        {showNavbarAndFooter && <Footer />}
      </div>
    </ContractProvider>
  );
}

export default App;